import React, { useState } from "react";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";

// components
import Button from "../../Button/Button";

// styles
import * as styles from "./ConditionsReverse.module.css";

const ConditionsReverse = ({ onCtaClick }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // State to track flipped cards
  const [flippedCards, setFlippedCards] = useState({
    hypertension: false,
    fattyLiver: false,
    diabetes: false,
    pcos: false,
    obesity: false,
  });

  // Handle card flip
  const handleCardFlip = (cardId, condition) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));

    // Call onCtaClick when flipping the card
    if (onCtaClick) {
      onCtaClick(null, condition);
    }
  };

  // Handle CTA click
  const handleCtaClick = (e, condition) => {
    if (onCtaClick) {
      onCtaClick(e, condition);
    }
  };

  return (
    <section className={styles.conditionsWrapper}>
      <div
        ref={ref}
        className={`container scroll_animate ${inView ? "inview" : ""}`}
      >
        {/* Section Header */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Conditions We Help Reverse</h2>
          <p className={styles.sectionDescription}>
            Twin Health heals dysfunctional metabolism at its source using the
            AI Digital Twin™, rather than just treating conditions. Members
            naturally achieve diabetes remission, PMOS improvement, weight
            reduction, and normalized blood pressure.
          </p>
        </div>

        {/* Cards Grid */}
        <div className={styles.cardsGrid}>
          {/* Card 1: Fatty Liver */}
          <div
            className={`${styles.cardContainer} ${styles.fattyLiverCard} ${
              flippedCards.fattyLiver ? styles.flipped : ""
            }`}
          >
            {/* Front Side */}
            <div className={`${styles.card} ${styles.cardFront}`}>
              <div className={styles.cardBackground}>
                <StaticImage
                  src="../../../images/home/liver_banner.png"
                  alt="Fatty Liver"
                  placeholder="blurred"
                  className={styles.backgroundImage}
                  loading="lazy"
                />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTextContent}>
                  <h3 className={styles.cardTitle}>Fatty Liver</h3>
                  <p className={styles.cardSubtitle}>
                    Reverse liver damage with scientifically-backed lifestyle
                    interventions
                  </p>
                </div>
                <div
                  className={styles.cardArrow}
                  onClick={() => handleCardFlip("fattyLiver", "Fatty Liver")}
                  onKeyDown={e =>
                    e.key === "Enter" &&
                    handleCardFlip("fattyLiver", "Fatty Liver")
                  }
                  role="button"
                  tabIndex={0}
                >
                  <StaticImage
                    src="../../../images/icons/header_cta_icn.png"
                    alt="Arrow"
                    placeholder="blurred"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Back Side */}
            <div className={`${styles.card} ${styles.cardBack}`}>
              <div className={styles.cardBackContent}>
                <h3 className={styles.cardBackTitle}>Fatty Liver</h3>
                <p className={styles.cardBackDescription}>
                  Repair your liver with science-backed lifestyle insights. Your
                  AI Digital Twin tracks your metabolism and guides small daily
                  actions that improve liver health and energy levels.
                </p>
                <Button
                  text="Start your journey today"
                  href="/wbdt-care-plan/#wbdt-form"
                  variant="tertiary"
                  onClick={e => handleCtaClick(e, "Fatty Liver")}
                  btnClass={styles.cardCta}
                />
              </div>
            </div>
          </div>

          {/* Card 2: Diabetes */}
          <div
            className={`${styles.cardContainer} ${styles.diabetesCard} ${
              flippedCards.diabetes ? styles.flipped : ""
            }`}
          >
            {/* Front Side */}
            <div className={`${styles.card} ${styles.cardFront}`}>
              <div className={styles.cardBackground}>
                <StaticImage
                  src="../../../images/home/diabetes_banner.png"
                  alt="Type 2 Diabetes and Pre-diabetes"
                  placeholder="blurred"
                  className={styles.backgroundImage}
                  loading="lazy"
                />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTextContent}>
                  <h3 className={styles.cardTitle}>
                    Type 2 Diabetes and Pre-diabetes
                  </h3>
                  <p className={styles.cardSubtitle}>
                    Normalize blood sugar & reduce/eliminate medications,
                    including insulin with our Diabetes Reversal Program India.
                  </p>
                </div>
                <div
                  className={styles.cardArrow}
                  onClick={() => handleCardFlip("diabetes", "Diabetes")}
                  onKeyDown={e =>
                    e.key === "Enter" && handleCardFlip("diabetes", "Diabetes")
                  }
                  role="button"
                  tabIndex={0}
                >
                  <StaticImage
                    src="../../../images/icons/header_cta_icn.png"
                    alt="Arrow"
                    placeholder="blurred"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Back Side */}
            <div className={`${styles.card} ${styles.cardBack}`}>
              <div className={styles.cardBackContent}>
                <h3 className={styles.cardBackTitle}>
                  Type 2 Diabetes & Pre-diabetes
                </h3>
                <p className={styles.cardBackDescription}>
                  Take control of your health beyond sugar readings. Your AI
                  Digital Twin identifies food, activity, and sleep patterns
                  that impact your glucose in real time, helping you reduce or
                  even stop medications naturally from your home.
                </p>
                <Button
                  text="Start your journey today"
                  href="/wbdt-care-plan/#wbdt-form"
                  variant="tertiary"
                  onClick={e => handleCtaClick(e, "Diabetes")}
                  btnClass={styles.cardCta}
                />
              </div>
            </div>
          </div>

          {/* Card 3: Hypertension */}
          <div
            className={`${styles.cardContainer} ${styles.hypertensionCard} ${
              flippedCards.hypertension ? styles.flipped : ""
            }`}
          >
            {/* Front Side */}
            <div
              className={`${styles.card} ${styles.cardFront} ${styles.hypertensionBg}`}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardTextContent}>
                  <h3 className={styles.cardTitle}>Hypertension</h3>
                  <p className={styles.cardSubtitle}>
                    Lower blood pressure naturally without long-term medications
                  </p>
                  <div className={styles.cardIcon}>
                    <StaticImage
                      src="../../../images/icons/heart_icon.png"
                      alt="Heart Icon"
                      placeholder="blurred"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div
                  className={styles.cardArrow}
                  onClick={() => handleCardFlip("hypertension", "Hypertension")}
                  onKeyDown={e =>
                    e.key === "Enter" &&
                    handleCardFlip("hypertension", "Hypertension")
                  }
                  role="button"
                  tabIndex={0}
                >
                  <StaticImage
                    src="../../../images/icons/header_cta_icn.png"
                    alt="Arrow"
                    placeholder="blurred"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Back Side */}
            <div className={`${styles.card} ${styles.cardBack}`}>
              <div className={styles.cardBackContent}>
                <h3 className={styles.cardBackTitle}>Hypertension</h3>
                <p className={styles.cardBackDescription}>
                  Lower BP through our Hypertension Reversal Program Online.
                  With daily guidance on nutrition, activity, and stress
                  management, your Twin helps you regain control, without
                  long-term medication.
                </p>
                <div
                  className={`d-flex align-items-center ${styles.cardBackFooter}`}
                >
                  <div className={styles.cardIcon}>
                    <StaticImage
                      src="../../../images/icons/heart_icon.png"
                      alt="Heart Icon"
                      placeholder="blurred"
                      loading="lazy"
                    />
                  </div>
                  <Button
                    text="Start your journey today"
                    href="/wbdt-care-plan/#wbdt-form"
                    variant="tertiary"
                    onClick={e => handleCtaClick(e, "Hypertension")}
                    btnClass={styles.cardCta}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: PMOS */}
          <div
            className={`${styles.cardContainer} ${styles.pcosCard} ${
              flippedCards.pcos ? styles.flipped : ""
            }`}
          >
            {/* Front Side */}
            <div className={`${styles.card} ${styles.cardFront}`}>
              <div
                className={`${styles.cardBackground} ${styles.pcosBackground}`}
              >
                <StaticImage
                  src="../../../images/home/pcos_banner.png"
                  alt="PMOS & Hormonal Imbalances"
                  placeholder="blurred"
                  className={styles.backgroundImage}
                  loading="lazy"
                />
              </div>
              <div className={`${styles.cardContent} ${styles.pcosContent}`}>
                <div className={styles.cardTextContent}>
                  <h3 className={styles.cardTitle}>
                    PMOS & Hormonal Imbalances
                  </h3>
                  <p className={styles.cardSubtitle}>
                    Treat PMOS, thyroid, and women's health issues
                  </p>
                </div>
                <div
                  className={styles.cardArrow}
                  onClick={() => handleCardFlip("pmos", "PMOS")}
                  onKeyDown={e =>
                    e.key === "Enter" && handleCardFlip("pmos", "PMOS")
                  }
                  role="button"
                  tabIndex={0}
                >
                  <StaticImage
                    src="../../../images/icons/header_cta_icn.png"
                    alt="Arrow"
                    placeholder="blurred"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Back Side */}
            <div className={`${styles.card} ${styles.cardBack}`}>
              <div className={styles.cardBackContent}>
                <h3 className={styles.cardBackTitle}>
                  PMOS & Hormonal Imbalances
                </h3>
                <p className={styles.cardBackDescription}>
                  Rebalance your hormones from the inside out. Twin Health helps
                  you manage PMOS, thyroid, and other hormonal issues through
                  data-backed nutrition, activity, and sleep guidance.
                </p>
                <Button
                  text="Start your journey today"
                  href="/wbdt-care-plan/#wbdt-form"
                  variant="tertiary"
                  onClick={e => handleCtaClick(e, "PMOS")}
                  btnClass={styles.cardCta}
                />
              </div>
            </div>
          </div>

          {/* Card 5: Obesity */}
          <div
            className={`${styles.cardContainer} ${styles.obesityCard} ${
              flippedCards.obesity ? styles.flipped : ""
            }`}
          >
            {/* Front Side */}
            <div
              className={`${styles.card} ${styles.cardFront} ${styles.obesityBg}`}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardTextContent}>
                  <h3 className={styles.cardTitle}>
                    Obesity & Weight Management
                  </h3>
                  <p className={styles.cardSubtitle}>
                    Sustainable fat loss without restrictive diets without
                    muscle or bone density loss
                  </p>
                  <div className={styles.cardIcon}>
                    <StaticImage
                      src="../../../images/icons/meter_icn.png"
                      alt="Weight Scale Icon"
                      placeholder="blurred"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div
                  className={styles.cardArrow}
                  onClick={() => handleCardFlip("obesity", "Obesity")}
                  onKeyDown={e =>
                    e.key === "Enter" && handleCardFlip("obesity", "Obesity")
                  }
                  role="button"
                  tabIndex={0}
                >
                  <StaticImage
                    src="../../../images/icons/header_cta_icn.png"
                    alt="Arrow"
                    placeholder="blurred"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Back Side */}
            <div className={`${styles.card} ${styles.cardBack}`}>
              <div className={styles.cardBackContent}>
                <h3 className={styles.cardBackTitle}>
                  Obesity & Weight Management
                </h3>
                <p className={styles.cardBackDescription}>
                  Sustainable fat loss that supports Metabolic Health. Your AI
                  Digital Twin creates a plan tailored to your metabolism,
                  helping you lose fat, maintain muscle, and feel more energetic
                  every day.
                </p>
                <div
                  className={`d-flex align-items-center ${styles.cardBackFooter}`}
                >
                  <div className={styles.cardIcon}>
                    <StaticImage
                      src="../../../images/icons/meter_icn.png"
                      alt="Weight Scale Icon"
                      placeholder="blurred"
                      loading="lazy"
                    />
                  </div>
                  <Button
                    text="Start your journey today"
                    href="/wbdt-care-plan/#wbdt-form"
                    variant="tertiary"
                    onClick={e => handleCtaClick(e, "Obesity")}
                    btnClass={styles.cardCta}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConditionsReverse;
